import { Controller, useForm } from 'react-hook-form'
import type { IContractItem, IContractItemFile } from 'types/contracts'
import {
  Box,
  Button,
  Dialog,
  Divider,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { FileUpload } from 'components/FileUpload'
import { useState } from 'react'
import { TFunction } from 'i18next'
import { TenderSearch } from 'pages/tender-search'
import CloseIcon from '@mui/icons-material/Close'
import { ITender } from 'types/tender'
import {
  useCreateContractMutation,
  useUpdateContractMutation,
} from 'services/contracts'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'
import _ from 'lodash/fp'

interface Props {
  initialValues?: Partial<IContractItem>
  onCancel: () => void
  canEdit: boolean
}

function createSchema(t: TFunction) {
  return z.object({
    contractNumber: z.string({
      required_error: t('required'),
    }),
    amount: z.string({
      required_error: t('required'),
    }),
    contractStartDate: z.string({
      required_error: t('required'),
    }),
    contractEndDate: z.string({
      required_error: t('required'),
    }),
  })
}

const dateFormat = 'YYYY-MM-DD'

export function ContractForm({ initialValues, onCancel, canEdit }: Props) {
  const { t } = useTranslation()
  const validationSchema = createSchema(t)
  const { handleSubmit, control, formState, reset } = useForm<IContractItem>({
    defaultValues: {
      ...initialValues,
      contractStartDate: initialValues?.contractStartDate
        ? dayjs(Number(initialValues.contractStartDate) * 1000).format(dateFormat)
        : undefined,
      contractEndDate: initialValues?.contractEndDate
        ? dayjs(Number(initialValues.contractEndDate) * 1000).format(dateFormat)
        : undefined,
      amount: _.isNil(initialValues?.amount)
        ? ''
        : initialValues?.amount?.toString(),
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    disabled: !canEdit,
  })
  const isEdit = !!initialValues
  const [isTenderSearchOpen, setTenderSearchOpen] = useState(false)
  const { errors } = formState
  const [files, setFiles] = useState<IContractItemFile[]>(
    initialValues?.contractFiles ?? []
  )
  const [selectedTender, setSelectedTender] = useState<ITender>()
  const [createContract, { isLoading: isCreateLoading }] =
    useCreateContractMutation()
  const [updateContract, { isLoading: isUpdateLoading }] =
    useUpdateContractMutation()

  const handleClose = () => {
    onCancel()
    reset()
  }

  const handleFormSubmit = (values: IContractItem) => {
    if (isEdit) {
      updateContract({
        ...initialValues,
        ...values,
        ...(selectedTender && { lotId: selectedTender.lotId }),
        contractStartDate: dayjs(values.contractStartDate).toISOString(),
        contractEndDate: dayjs(values.contractEndDate).toISOString(),
        contractFiles: files.map((file) => ({
          ...file,
          contractId: initialValues?.id ?? '',
        })),
      }).then((res) => {
        if ('error' in res) {
          toast.error(t('contract-create-fail'))
        } else {
          toast.success(t('operation-success'))
          handleClose()
        }
      })
    } else {
      const id = uuidv4()

      createContract({
        ...values,
        lotId: selectedTender?.lotId,
        contractStartDate: dayjs(values.contractStartDate).toISOString(),
        contractEndDate: dayjs(values.contractEndDate).toISOString(),
        id,
        contractFiles: files.map((file) => ({
          ...file,
          contractId: id,
        })),
      }).then((res) => {
        if ('error' in res) {
          toast.error(t('contract-create-fail'))
        } else {
          toast.success(t('operation-success'))
          onCancel()
        }
      })
    }
  }

  return (
    <>
      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <Box sx={{ pt: 1 }}>
            <Controller
              name="contractNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  label={t('contract-number')}
                />
              )}
            />
            {errors.contractNumber && (
              <FormHelperText error>{errors.contractNumber?.message}</FormHelperText>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ pt: 1 }}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  fullWidth
                  label={t('contract-amount')}
                />
              )}
              name="amount"
              control={control}
            />
            {errors.amount && (
              <FormHelperText error>{errors.amount?.message}</FormHelperText>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ pt: 1 }}>
            <Button
              onClick={() => setTenderSearchOpen(true)}
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
              disabled={!canEdit}
            >
              {selectedTender
                ? selectedTender.name
                : initialValues?.tenderName || t('select-tender')}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Typography>{t('contract-duration')}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="contractStartDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={field.value ? dayjs(field.value) : null}
                inputRef={field.ref}
                label={t('contract-date')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                  field: { clearable: true },
                }}
                onChange={(date) => {
                  if (date) {
                    field.onChange(dayjs(date).format(dateFormat))
                  } else {
                    field.onChange(null)
                  }
                }}
              />
            )}
          />
          {errors.contractStartDate && (
            <FormHelperText error>
              {errors.contractStartDate?.message}
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="contractEndDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={field.value ? dayjs(field.value) : null}
                inputRef={field.ref}
                label={t('contract-end-date')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                  field: { clearable: true },
                }}
                onChange={(date) => {
                  if (date) {
                    field.onChange(dayjs(date).format(dateFormat))
                  } else {
                    field.onChange(null)
                  }
                }}
              />
            )}
          />
          {errors.contractEndDate && (
            <FormHelperText error>{errors.contractEndDate?.message}</FormHelperText>
          )}
        </Grid>

        <Box sx={{ px: 1.5, mt: 1 }}>
          {files.map((file) => (
            <Box sx={{ mt: 1 }} key={file.fileId}>
              <FileUpload
                onDelete={() =>
                  setFiles((old) => old.filter((o) => o.fileId !== file.fileId))
                }
                text={file.displayName ?? ''}
                fileId={file.fileId}
                disableUpload
                disableDelete={!canEdit}
              />
            </Box>
          ))}
        </Box>

        {canEdit && (
          <Grid xs={12} item>
            <FileUpload
              text={t('add')}
              disableDelete
              ignoreApiFile
              handleFile={(file) => {
                setFiles((old) => [
                  ...old,
                  {
                    contractId: '',
                    fileId: file.id,
                  },
                ])
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" mt={2} justifyContent="flex-end" gap={1}>
            <Button onClick={handleClose}>
              {canEdit ? t('cancel') : t('close')}
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                disabled={isCreateLoading || isUpdateLoading}
                onClick={handleSubmit(handleFormSubmit)}
              >
                {isEdit ? t('save') : t('add')}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
      {isTenderSearchOpen && (
        <Dialog
          fullScreen
          open={isTenderSearchOpen}
          onClose={() => setTenderSearchOpen(false)}
        >
          <Box p={1}>
            <Box display="flex" justifyContent="flex-end">
              <CloseIcon onClick={() => setTenderSearchOpen(false)} />
            </Box>
            <TenderSearch
              forInnerSearch
              onSelectItem={(t) => {
                setSelectedTender(t)
                setTenderSearchOpen(false)
              }}
            />
          </Box>
        </Dialog>
      )}
    </>
  )
}
